import { useState } from 'preact/hooks'
import { UserPlus, X } from 'lucide-preact'
import { composeAuthEmail, isPlausibleEmail, isValidUsername, normalizeUsername } from '../lib/auth-email'
import type { NewMember } from '../store/types'

/**
 * "Ajouter un membre": the one place a person is added to the household.
 *
 * Three fields, and the third is the one that needs explaining on screen rather
 * than only in a comment. Supabase Auth requires a unique address per account
 * and a family has one inbox, so the username is what makes two people out of
 * one mailbox: `lea` + `papa@x.be` becomes the account `papa+lea@x.be`, and the
 * code lands in papa's inbox. The composed address is echoed live under the
 * fields, because it is what the member will have to type back months later and
 * a household should see it once before it is mailed anywhere.
 *
 * Adding is a store action and works offline. Sending the invitation is a
 * separate button on the row this creates — the two are not the same act, and
 * pretending they are would mean a member added on a train silently failing to
 * exist.
 */

/** Leave both blank for a member who will never sign in — a profile, not a login. */
export function MemberForm({
  onAdd,
  onClose,
}: {
  onAdd: (member: NewMember) => void
  onClose: () => void
}) {
  const [displayName, setDisplayName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')

  const name = displayName.trim()
  const handle = normalizeUsername(username)
  const address = email.trim()

  const wantsLogin = Boolean(handle || address)
  const authEmail = wantsLogin ? composeAuthEmail(address, handle) : ''

  // Say which half is wrong rather than disabling a button with no explanation:
  // "the form will not submit and I cannot see why" is the worst state here.
  const problem = !wantsLogin
    ? ''
    : !isPlausibleEmail(address)
      ? 'Adresse e-mail incomplète.'
      : handle && !isValidUsername(handle)
        ? 'Nom d’utilisateur : 2 à 20 caractères, minuscules, chiffres et tirets.'
        : ''

  const ready = Boolean(name) && !problem

  function submit(event: Event) {
    event.preventDefault()
    if (!ready) return

    onAdd({
      displayName: name,
      username: handle,
      authEmail,
      // Editorial, and nothing branches on it — the household's owner is
      // `households.owner_user_id`, which no client can write.
      role: 'child',
    })

    onClose()
  }

  return (
    <form class="task-form" onSubmit={submit}>
      <div class="task-form-heading">
        <strong>Nouveau membre</strong>
        <button type="button" class="icon-button small" onClick={onClose} aria-label="Annuler">
          <X size={18} />
        </button>
      </div>

      <label class="task-form-field">
        <span>Prénom</span>
        <input
          type="text"
          value={displayName}
          maxLength={40}
          placeholder="Léa"
          autofocus
          onInput={(event) => setDisplayName((event.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label class="task-form-field">
        <span>Nom d’utilisateur</span>
        <input
          type="text"
          value={username}
          maxLength={20}
          placeholder="lea"
          autocomplete="off"
          onInput={(event) => setUsername((event.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label class="task-form-field">
        <span>Adresse e-mail</span>
        <input
          type="email"
          value={email}
          placeholder="papa@exemple.be"
          autocomplete="off"
          onInput={(event) => setEmail((event.currentTarget as HTMLInputElement).value)}
        />
      </label>

      {authEmail && !problem && (
        <p class="notice muted">
          Son compte sera <strong>{authEmail}</strong> — le code arrive dans la boîte de{' '}
          <strong>{address}</strong>.
        </p>
      )}

      {problem && <p class="login-error">{problem}</p>}

      {!wantsLogin && (
        <p class="notice muted">
          Sans nom d’utilisateur ni e-mail, ce membre n’aura pas de compte : ses cases se cochent
          depuis cet appareil.
        </p>
      )}

      <button class="primary" type="submit" disabled={!ready}>
        <UserPlus size={18} />
        Ajouter
      </button>
    </form>
  )
}
